import axios from 'axios';
import { API_URL } from '../constantes/constantes';
import { withTokenRetry } from './apiWrapper';

/**
 * Servicio para obtener sugerencias de autocompletado de búsqueda
 * 
 * @description Solicita sugerencias predictivas basadas en un prefijo de texto.
 * Las sugerencias ayudan a los usuarios a completar sus términos de búsqueda
 * y mejorar la experiencia de usuario. Soporta filtrado por categoría y
 * requiere autenticación por token.
 * 
 * @async
 * @function getSuggestions
 * 
 * @param {string} query - Prefijo de texto para generar sugerencias (mínimo 3 caracteres)
 * @param {string} token - Token de autenticación Bearer
 * @param {string} category - Categoría para filtrar sugerencias (opcional, puede ser string vacío)
 * 
 * @returns {Promise<Object>} Objeto con sugerencias y código de estado
 * @returns {Promise<{data: Array|Object, statusCode: number}>} Estructura de respuesta:
 *   - data: Array de objetos de sugerencia o array vacío en caso de error
 *   - data[].sugerencia: Texto de la sugerencia completa
 *   - data[].relevancia: Puntuación de relevancia (opcional)
 *   - statusCode: Código HTTP de respuesta (200, 401, 404)
 * 
 * @throws {Object} Retorna objeto con statusCode 401 en caso de error de red
 * 
 * @example
 * // Sugerencias básicas
 * const result = await getSuggestions('mex', token, '');
 * if (result.statusCode === 200) {
 *   console.log('Sugerencias:', result.data);
 *   // [
 *   //   { sugerencia: 'mexico noticias' },
 *   //   { sugerencia: 'mexico vs argentina' },
 *   //   { sugerencia: 'mexico economia' }
 *   // ]
 * }
 * 
 * @example
 * // Sugerencias con filtro de categoría
 * const result = await getSuggestions('fut', token, 'deportes');
 * if (result.statusCode === 200) {
 *   result.data.forEach(item => {
 *     console.log('Sugerencia deportiva:', item.sugerencia);
 *   });
 * }
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ../constantes/constantes.js} Para configuración de API_URL
 * @see {@link ./postToken.js} Para obtener token de autenticación
 * @see {@link ../components/sugerencias/Sugerencias.js} Componente que consume este servicio
 */
const _getSuggestions = async (query, category, token) => {
  // Validación de parámetros
  if (!query || query.length < 3 || !token) {
    return { statusCode: 400, data: [] };
  }

  // Validación de URL para prevenir SSRF
  const allowedDomains = ['unotv.com', 'clarosports.com', 'amxinfra.com'];
  const isValidURL = allowedDomains.some(domain => API_URL.includes(domain));
  
  if (!isValidURL) {
    console.error('URL no autorizada:', API_URL);
    return { statusCode: 403, data: [] };
  }

  // Construcción de URL con parámetros codificados
  const encodedQuery = encodeURIComponent(query);
  const encodedCategory = encodeURIComponent(category || '');
  const apiURL = `${API_URL}search_suggest?prefix=${encodedQuery}&category=${encodedCategory}`;

  const config = {
    method: 'GET',
    url: apiURL,
    timeout: 25000,
    headers: {
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  };

  try {
    const response = await axios(config);

    // Mapeo de códigos de respuesta
    const statusHandlers = {
      200: () => ({ data: response.data, statusCode: 200 }),
      401: () => ({ statusCode: 401, data: [] }), // Token inválido/expirado
      404: () => ({ statusCode: 404, data: [] })  // Endpoint no encontrado
    };

    const handler = statusHandlers[response.status];
    return handler ? handler() : { statusCode: response.status, data: [] };
    
  } catch (error) {
    // Manejo de errores de red/timeout
    if (error.response) {
      return { statusCode: error.response.status, data: [] };
    }
    
    // Error de red o timeout
    return { statusCode: 401, data: [] };
  }
};

export const getSuggestions = withTokenRetry(_getSuggestions);